import {
  useContext,
  createContext,
  PropsWithChildren,
  useCallback,
  useRef,
} from 'react';
import useField from '__hooks/useField';

export const MAX_DESCRIPTION_LENGTH = 500;

type UserDescriptionContext = {
  userDescription: string | null;
  setUserDescription: (val: string) => void;
  checkDescriptionChanged: () => boolean;
  clearUserDescription: () => void;
};

type UserDescriptionProviderProps = {
  step?: any;
};

const nullishToString = (val?: string) => val ?? '';

export const context = createContext<UserDescriptionContext>({
  userDescription: null,
  setUserDescription: () => {},
  checkDescriptionChanged: () => false,
  clearUserDescription: () => {},
});

export const useUserDescriptionContext = () => useContext(context);

export const UserDescriptionProvider = ({
  children,
  step,
}: PropsWithChildren<UserDescriptionProviderProps>) => {
  const [userDescription, setUserDescription] = useField(
    nullishToString(step?.userDescription)
  );

  const originalUserDescription = useRef<string | undefined>('');

  if (
    step?.userDescription &&
    originalUserDescription.current !== nullishToString(step?.userDescription)
  ) {
    originalUserDescription.current = nullishToString(step.userDescription);
  }

  const limitSize = useCallback(
    (val: string) => {
      setUserDescription(val.slice(0, MAX_DESCRIPTION_LENGTH));
    },
    [setUserDescription]
  );

  const checkDescriptionChanged = useCallback(() => {
    return userDescription !== originalUserDescription.current;
  }, [userDescription]);

  const clearUserDescription = useCallback(() => {
    originalUserDescription.current = '';
    setUserDescription('');
  }, [setUserDescription, originalUserDescription]);

  const ctx = {
    userDescription,
    setUserDescription: limitSize,
    checkDescriptionChanged,
    clearUserDescription,
  };

  return <context.Provider value={ctx}>{children}</context.Provider>;
};
